import { Box, IconButton, TableBody, TableCell, TableRow } from '@mui/material'
import { Actions, Field } from './types'
import { GenericTooltip } from '../generic-tooltip'
import { useTranslation } from 'react-i18next'
import { Skeleton } from '@material-ui/lab'

// Lado de la caja en la que se encaja cada icono de accion. 24 es el mayor de
// los svg que hay hoy (assign-form), asi que ninguno se agranda de mas.
const ICON_SIZE = 24

export type BodyProps<T> = {
  readonly fields: Field<T>[]
  readonly items: T[]
  readonly rowKeyField: keyof T
  readonly actions?: Actions<T>
  readonly isLoading?: boolean
}

export function Body<T extends { [key: string]: any }>(props: BodyProps<T>) {
  const { t } = useTranslation()

  // preserveHiddenSpace reserva el hueco de una accion oculta para que los
  // iconos queden en la misma columna en todas las filas. Eso solo tiene
  // sentido cuando la accion se ve en unas filas y en otras no: si esta oculta
  // en TODAS, el hueco no alinea nada y solo deja un espacio muerto en medio.
  const hiddenInEveryRow = (props.actions?.items ?? []).map(
    (a) => !!a.hidden && props.items.length > 0 && props.items.every((item) => a.hidden!(item))
  )

  const rows = props.items.map((item, i) => {
    return !props.isLoading ? (
      <TableRow key={item[props.rowKeyField] + i}>
        {Object.values(props.fields).map((field, idx) => {
          return (
            <TableCell
              style={{
                width: `${100 / Object.keys(props.fields).length}%`,
                marginLeft: 4,
              }}
              key={(field.name as string) + idx + 'cell'}
            >
              {field.renderFunc
                ? field.renderFunc(field, item)
                : item[field.name]?.toString() || ''}
            </TableCell>
          )
        })}

        {props.actions && props.actions.items?.length > 0 && (
          <TableCell
            style={{
              width: `${100 / Object.keys(props.fields).length}%`,
              marginLeft: 4,
            }}
            key={item[props.rowKeyField] + 'cell'}
          >
            <Box
              key={item[props.rowKeyField] + 'box'}
              display="flex"
              justifyContent="space-between"
              gap={1}
            >
              {props.actions.items.map((a, idx) => {
                const isHidden = a.hidden && a.hidden(item)

                if (isHidden && (!props.actions?.preserveHiddenSpace || hiddenInEveryRow[idx])) {
                  return null
                }

                return (
                  <Box
                    key={`action-${idx}`}
                    style={{
                      visibility: isHidden ? 'hidden' : 'visible',
                      display: 'inline-block',
                    }}
                  >
                    <GenericTooltip
                      values={[t(a.label || (typeof a.icon === 'string' ? a.icon : ''))]}
                      icon={
                        typeof a.icon === 'string' ? (
                          <img
                            src={a.icon}
                            alt={a.label ? t(a.label) : ''}
                            key={item[props.rowKeyField]}
                            onClick={() => !isHidden && a.handler(item)}
                            // Cada svg de accion trae un tamano distinto (view 21x15,
                            // edit 16x22, download 25x19...), asi que sin caja comun
                            // la fila queda desalineada. contain los encaja sin
                            // deformar ninguno.
                            style={{
                              width: ICON_SIZE,
                              height: ICON_SIZE,
                              objectFit: 'contain',
                              pointerEvents: isHidden ? 'none' : 'auto',
                              cursor: 'pointer',
                            }}
                          />
                        ) : (
                          <IconButton
                            size="small"
                            key={item[props.rowKeyField]}
                            onClick={() => !isHidden && a.handler(item)}
                            sx={{
                              p: 0.5,
                              color: a.destructive ? 'var(--destructive, #e14c54)' : '#9b9d9a',
                              pointerEvents: isHidden ? 'none' : 'auto',
                            }}
                          >
                            <a.icon sx={{ fontSize: 20 }} />
                          </IconButton>
                        )
                      }
                      noUseFab={true}
                    />
                  </Box>
                )
              })}
            </Box>
          </TableCell>
        )}
      </TableRow>
    ) : (
      <TableRow key={item[props.rowKeyField] + i}>
        {Object.values(props.fields).map((field, idx) => {
          return (
            <TableCell key={(field.name as string) + idx + 'cell'}>
              <Skeleton />
            </TableCell>
          )
        })}

        {props.actions && props.actions.items?.length > 0 && (
          <TableCell key={item[props.rowKeyField] + i + 'cellq'}>
            <Box display="flex" justifyContent="space-between">
              {props.actions.items.map((a, idx) => {
                if (a.hidden && a.hidden(item)) {
                  return null
                }
                return (
                  <GenericTooltip
                    key={`skeleton-${idx}`}
                    values={[t(a.label || (typeof a.icon === 'string' ? a.icon : ''))]}
                    icon={<Skeleton variant="circle" width={24} height={24} />}
                    noUseFab={true}
                  />
                )
              })}
            </Box>
          </TableCell>
        )}
      </TableRow>
    )
  })

  return <TableBody>{rows}</TableBody>
}
